import { create } from "zustand";
import { syncProDocumentChromeForTheme } from "@/lib/pro-document-chrome";

export type ProTheme = "dark" | "light";
export type ProThemePreference = "dark" | "light" | "system";

const STORAGE_KEY = "slotique-pro-theme";

interface ProThemeState {
  theme: ProTheme;
  preference: ProThemePreference;
  setPreference: (preference: ProThemePreference) => void;
}

function getSystemTheme(): ProTheme {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function resolveTheme(preference: ProThemePreference): ProTheme {
  if (preference === "system") return getSystemTheme();
  return preference;
}

export function readStoredPreference(): ProThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
    return "system";
  } catch {
    return "system";
  }
}

function writeStoredPreference(preference: ProThemePreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // happy-dom / private mode — ignore
  }
}

let systemMediaQuery: MediaQueryList | null = null;
let systemListener: ((event: MediaQueryListEvent) => void) | null = null;

function detachSystemThemeListener(): void {
  if (systemMediaQuery && systemListener) {
    systemMediaQuery.removeEventListener("change", systemListener);
  }
  systemMediaQuery = null;
  systemListener = null;
}

function attachSystemThemeListener(): void {
  if (typeof window === "undefined") return;
  detachSystemThemeListener();
  systemMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  systemListener = () => {
    const { preference } = useProThemeStore.getState();
    if (preference !== "system") return;
    const theme = getSystemTheme();
    useProThemeStore.setState({ theme });
    syncProDocumentChromeForTheme(theme);
  };
  systemMediaQuery.addEventListener("change", systemListener);
}

function applyPreference(preference: ProThemePreference): void {
  if (preference === "system") {
    attachSystemThemeListener();
  } else {
    detachSystemThemeListener();
  }
  const theme = resolveTheme(preference);
  useProThemeStore.setState({
    preference,
    theme,
  });
  syncProDocumentChromeForTheme(theme);
}

export const useProThemeStore = create<ProThemeState>(() => ({
  theme: "dark",
  preference: "system",
  setPreference: (preference) => {
    writeStoredPreference(preference);
    applyPreference(preference);
  },
}));

let hydrated = false;

/** Restore theme from localStorage on the client (safe to call in useEffect). */
export function hydrateProThemeFromStorage(): void {
  if (hydrated || typeof window === "undefined") return;
  hydrated = true;
  const preference = readStoredPreference();
  applyPreference(preference);
}

export function proThemeClassName(theme: ProTheme): "pro-dark" | "pro-light" {
  return theme === "light" ? "pro-light" : "pro-dark";
}

/** @internal Test-only reset for hydrate guard and system listener. */
export function resetProThemeStoreForTests(): void {
  hydrated = false;
  detachSystemThemeListener();
}
